import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ReactComponent as AlgovexWordmark } from '../assets/algovex-wordmark-dark.svg';
import './LandingPage.css';

const FEATURES = [
  {
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
        <circle cx="16" cy="16" r="13" stroke="var(--teal)" strokeWidth="1.5" fill="none" strokeDasharray="2 4" opacity="0.5"/>
        <path d="M16 16 L22 9" stroke="var(--amber)" strokeWidth="2" strokeLinecap="round"/>
        <circle cx="16" cy="16" r="2.5" fill="var(--teal)"/>
        <circle cx="16" cy="5" r="1.3" fill="var(--blue)"/>
        <circle cx="27" cy="16" r="1.3" fill="var(--blue)"/>
        <circle cx="16" cy="27" r="1.3" fill="var(--blue)"/>
        <circle cx="5" cy="16" r="1.3" fill="var(--blue)"/>
      </svg>
    ),
    title: 'Macro Regime Tracking',
    desc: "See the market's growth, inflation, liquidity, and risk regime before you trade — not just price.",
  },
  {
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
        <circle cx="16" cy="16" r="12" stroke="var(--teal)" strokeWidth="1.5" fill="none"/>
        <path d="M12 16 C12 10 20 10 20 16 C20 22 12 22 12 16Z" stroke="var(--amber)" strokeWidth="1.5" fill="none"/>
        <circle cx="16" cy="13" r="1.5" fill="var(--teal)"/>
        <circle cx="16" cy="19" r="1.5" fill="var(--amber)"/>
      </svg>
    ),
    title: 'AI Research Analyst',
    desc: 'Ask about tickers, macro data, filings, and news in plain English, then turn the answer into a strategy.',
  },
  {
    icon: (
      <svg width="32" height="32" viewBox="0 0 96 96" fill="none">
        <circle cx="48" cy="48" r="36" stroke="var(--teal)" strokeWidth="1.5" fill="none" strokeDasharray="56 170" strokeDashoffset="-24" opacity="0.7"/>
        <polyline points="18,48 28,48 33,32 40,62 47,38 54,54 60,42 68,42 74,48 78,48" stroke="var(--teal)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        <circle cx="48" cy="48" r="4" fill="var(--teal)"/>
      </svg>
    ),
    title: 'Visual Strategy Builder',
    desc: 'Drag-and-drop nodes to turn a macro thesis into trading logic, without writing a single line of code.',
  },
  {
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
        <rect x="2" y="14" width="4" height="14" rx="1" fill="var(--teal)" opacity="0.4"/>
        <rect x="9" y="8" width="4" height="20" rx="1" fill="var(--teal)" opacity="0.6"/>
        <rect x="16" y="4" width="4" height="24" rx="1" fill="var(--teal)" opacity="0.8"/>
        <rect x="23" y="10" width="4" height="18" rx="1" fill="var(--teal)"/>
        <path d="M4 12 L11 6 L18 2 L25 8" stroke="var(--blue)" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
      </svg>
    ),
    title: 'Instant Backtesting',
    desc: 'Run strategies against years of historical data in seconds. Stocks, crypto, forex — all supported.',
  },
];

const STATS = [
  { value: '< 5s', label: 'Avg backtest time' },
  { value: '30yr+', label: 'Historical data' },
  { value: '100%', label: 'Free to start' },
];

// Structured data (JSON-LD) for search engines + AI answer engines. Kept here
// (rendered into <Helmet>) so it's baked into the react-snap prerender of "/".
// Organization + SoftwareApplication power rich results; FAQPage is both a rich
// result and the most-quoted format for AI overviews (ChatGPT/Perplexity/Google AI).
const ORG_LD = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Algovex',
  url: 'https://algovex.ai/',
  logo: 'https://algovex.ai/logo512.png',
  description:
    'Algovex is a no-code platform for visually building, backtesting, and optimizing algorithmic trading strategies across stocks, forex, and crypto, with macro regime tracking and an AI research analyst.',
};

const SOFTWARE_LD = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Algovex',
  applicationCategory: 'FinanceApplication',
  operatingSystem: 'Web',
  url: 'https://algovex.ai/',
  description:
    'Build trading strategies with a drag-and-drop node graph (no code), backtest them against 30+ years of historical data across stocks, forex, and crypto, and export to TradingView PineScript or Python backtrader.',
  offers: [
    { '@type': 'Offer', name: 'Free', price: '0', priceCurrency: 'USD' },
    { '@type': 'Offer', name: 'Basic', price: '19', priceCurrency: 'USD', category: 'Monthly subscription' },
    { '@type': 'Offer', name: 'Pro', price: '49', priceCurrency: 'USD', category: 'Monthly subscription' },
  ],
};

const FAQ_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is Algovex?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Algovex is a no-code platform for building and backtesting algorithmic trading strategies. You assemble strategy logic with a drag-and-drop node graph, test it against historical stock, forex, and crypto data, and refine it with walk-forward analysis, a macro regime tracker, and an AI research analyst.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do I need to know how to code to use Algovex?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'No. Strategies are built visually by connecting nodes (indicators, price conditions, logic, and buy/sell actions), so no programming is required. If you do want code, Algovex can export any strategy to TradingView PineScript or Python backtrader.',
      },
    },
    {
      '@type': 'Question',
      name: 'What markets can I backtest on Algovex?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Algovex supports backtesting across stocks, forex, and crypto using decades of historical price data, with most backtests completing in a few seconds.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is Algovex free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Yes, Algovex is free to start — you can build and backtest strategies on the Free plan. Paid Basic ($19/mo) and Pro ($49/mo) plans raise daily limits and unlock unlimited backtests, AI strategy generation, and more.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can Algovex export strategies to TradingView or Python?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Yes. Any strategy you build can be exported to TradingView PineScript or Python backtrader, so you can take the same logic to another platform or run it yourself.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the macro regime tracker?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          "Algovex's macro regime tracker classifies the market's current growth, inflation, liquidity, and risk regime from macro and market data, so you can align strategies with the broader environment instead of trading on price alone.",
      },
    },
  ],
};

function AnimatedChart() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    let w, h;
    let animId;
    let running = false;
    let t = 0;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const generatePoints = (offset, amp, freq, count) => {
      const pts = [];
      for (let i = 0; i <= count; i++) {
        const x = (i / count) * w;
        const noise = Math.sin(i * freq + offset) * amp +
                      Math.sin(i * freq * 2.3 + offset * 1.7) * amp * 0.4 +
                      Math.sin(i * freq * 0.7 + offset * 0.5) * amp * 0.6;
        const y = h * 0.5 + noise;
        pts.push({ x, y });
      }
      return pts;
    };

    const drawLine = (pts, color, width, opacity) => {
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) {
        const xc = (pts[i - 1].x + pts[i].x) / 2;
        const yc = (pts[i - 1].y + pts[i].y) / 2;
        ctx.quadraticCurveTo(pts[i - 1].x, pts[i - 1].y, xc, yc);
      }
      ctx.strokeStyle = color;
      ctx.lineWidth = width;
      ctx.globalAlpha = opacity;
      ctx.stroke();
      ctx.globalAlpha = 1;
    };

    const drawGlow = (pts, color) => {
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) {
        const xc = (pts[i - 1].x + pts[i].x) / 2;
        const yc = (pts[i - 1].y + pts[i].y) / 2;
        ctx.quadraticCurveTo(pts[i - 1].x, pts[i - 1].y, xc, yc);
      }
      ctx.lineTo(w, h);
      ctx.lineTo(0, h);
      ctx.closePath();
      const grad = ctx.createLinearGradient(0, h * 0.3, 0, h);
      grad.addColorStop(0, color + '18');
      grad.addColorStop(1, color + '00');
      ctx.fillStyle = grad;
      ctx.fill();
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      t += 0.008;

      // Grid
      ctx.strokeStyle = '#1e2a2a';
      ctx.lineWidth = 0.5;
      ctx.globalAlpha = 0.4;
      for (let y = 0; y < h; y += 40) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke();
      }
      for (let x = 0; x < w; x += 60) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke();
      }
      ctx.globalAlpha = 1;

      const pts1 = generatePoints(t, h * 0.18, 0.08, 120);
      const pts2 = generatePoints(t * 0.6 + 2, h * 0.12, 0.06, 120);
      const pts3 = generatePoints(t * 1.2 + 5, h * 0.08, 0.1, 120);

      drawGlow(pts1, '#00d4aa');
      drawLine(pts1, '#00d4aa', 2, 0.9);
      drawLine(pts2, '#58a6ff', 1.5, 0.5);
      drawLine(pts3, '#d29922', 1, 0.3);

      // Moving dot on main line
      const dotIdx = Math.floor((((t * 8) % 120) + 120) % 120);
      const dot = pts1[dotIdx];
      if (dot) {
        ctx.beginPath();
        ctx.arc(dot.x, dot.y, 4, 0, Math.PI * 2);
        ctx.fillStyle = '#00d4aa';
        ctx.fill();
        ctx.beginPath();
        ctx.arc(dot.x, dot.y, 8, 0, Math.PI * 2);
        ctx.fillStyle = '#00d4aa22';
        ctx.fill();
      }

      if (running) animId = requestAnimationFrame(draw);
    };

    const start = () => {
      if (running) return;
      running = true;
      draw();
    };
    const stop = () => {
      running = false;
      cancelAnimationFrame(animId);
    };

    resize();
    window.addEventListener('resize', resize);

    if (prefersReducedMotion) {
      draw();
      return () => window.removeEventListener('resize', resize);
    }

    let isVisibleInViewport = false;
    const syncRunning = () => {
      if (isVisibleInViewport && document.visibilityState === 'visible') start();
      else stop();
    };
    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisibleInViewport = entry.isIntersecting;
        syncRunning();
      },
      { threshold: 0 }
    );
    observer.observe(canvas);
    document.addEventListener('visibilitychange', syncRunning);

    return () => {
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', syncRunning);
      observer.disconnect();
      stop();
    };
  }, []);

  return <canvas ref={canvasRef} className="landing-chart" />;
}

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="landing">
      <Helmet>
        <title>Algovex — Macro-First AI Trading Strategy Builder & Backtester</title>
        <meta name="description" content="Go from macro research to a backtested trading strategy in one seamless workflow. Track macro regimes, ask an AI analyst, build visually, backtest across stocks, forex, and crypto, then export to TradingView PineScript or Python backtrader — free to start." />
        <meta property="og:title" content="Algovex — Macro-First AI Strategy Builder & Backtester" />
        <meta property="og:description" content="Track macro regimes, research with an AI analyst, build strategies visually, backtest them instantly, and export to PineScript or backtrader — one seamless workflow, free to start." />
        <meta property="og:url" content="https://algovex.ai/" />
        <link rel="canonical" href="https://algovex.ai/" />
        <script type="application/ld+json">{JSON.stringify(ORG_LD)}</script>
        <script type="application/ld+json">{JSON.stringify(SOFTWARE_LD)}</script>
        <script type="application/ld+json">{JSON.stringify(FAQ_LD)}</script>
      </Helmet>
      <nav className="landing-nav">
        <div className="landing-nav-brand">
          <AlgovexWordmark style={{ width: 28, height: 28 }} />
          <span className="landing-nav-wordmark">algo<span className="teal">vex</span></span>
        </div>
        <div className="landing-nav-links">
          <button className="landing-nav-link landing-nav-scroll" onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}>Features</button>
          <button className="landing-nav-link landing-nav-scroll" onClick={() => document.getElementById('export')?.scrollIntoView({ behavior: 'smooth' })}>Export</button>
          <button className="landing-nav-link" onClick={() => navigate('/api-access')}>API</button>
          <button className="landing-nav-link" onClick={() => navigate('/auth')}>Sign In</button>
          <button className="landing-cta-sm" onClick={() => navigate('/auth')}>Get Started</button>
        </div>
      </nav>

      {/* Hero */}
      <section className="landing-hero">
        <div className="landing-hero-bg">
          <AnimatedChart />
        </div>
        <div className="landing-hero-content">
          <div className="landing-hero-badge">MACRO-FIRST · AI-POWERED</div>
          <h1 className="landing-hero-title">
            Start from the macro.<br />
            <span className="teal">End with a tested edge.</span>
          </h1>
          <p className="landing-hero-sub">
            Track the macro regime and ask the AI Analyst what's driving it, turn that thesis into a
            strategy with the visual builder, then backtest it in seconds. One seamless workflow, no code required.
          </p>
          <div className="landing-hero-actions">
            <button className="landing-btn-primary" onClick={() => navigate('/auth')}>
              Start Building — Free
            </button>
            <button className="landing-btn-secondary" onClick={() => navigate('/strategies')}>
              Try as Guest
            </button>
          </div>
          <div className="landing-stats">
            {STATS.map((s, i) => (
              <div key={i} className="landing-stat">
                <div className="landing-stat-value">{s.value}</div>
                <div className="landing-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="landing-features">
        <div className="landing-section-header">
          <h2>Everything you need to <span className="teal">backtest smarter</span></h2>
          <p>No code. No spreadsheets. Just drag, drop, and run.</p>
        </div>
        <div className="landing-features-grid">
          {FEATURES.map((f, i) => (
            <div key={i} className="landing-feature-card" style={{ animationDelay: `${i * 0.1}s` }}>
              <div className="landing-feature-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="landing-how">
        <div className="landing-section-header">
          <h2>From macro research to a <span className="teal">tested strategy</span></h2>
          <p>One seamless workflow — no code, no spreadsheets, no switching tools.</p>
        </div>
        <div className="landing-steps">
          <div className="landing-step">
            <div className="landing-step-num">01</div>
            <h3>Research</h3>
            <p>Track the macro regime and ask the AI Analyst about tickers, macro data, filings, and news to form your thesis.</p>
          </div>
          <div className="landing-step-divider" />
          <div className="landing-step">
            <div className="landing-step-num">02</div>
            <h3>Design</h3>
            <p>Drag indicator, condition, and action blocks onto a visual canvas to turn that thesis into strategy logic.</p>
          </div>
          <div className="landing-step-divider" />
          <div className="landing-step">
            <div className="landing-step-num">03</div>
            <h3>Backtest</h3>
            <p>Pick an asset, date range, and hit run. Results in seconds with full equity curves and trade logs.</p>
          </div>
          <div className="landing-step-divider" />
          <div className="landing-step">
            <div className="landing-step-num">04</div>
            <h3>Improve</h3>
            <p>AI analyzes your results and suggests parameter tweaks, new indicators, or entirely new strategies.</p>
          </div>
        </div>
      </section>

      {/* Export highlight */}
      <section id="export" className="landing-features">
        <div className="landing-section-header">
          <div className="landing-hero-badge" style={{ marginBottom: 16 }}>NEW</div>
          <h2>Export to <span className="teal">PineScript &amp; backtrader</span></h2>
          <p>
            Build visually on Algovex, then run it anywhere. Export any strategy to
            TradingView PineScript or Python backtrader in one click — ready-to-run
            code with your indicators, entry/exit rules, and stop-loss / take-profit
            wired in. No vendor lock-in.
          </p>
        </div>
        <div className="landing-features-grid">
          <div className="landing-feature-card">
            <h3>TradingView PineScript</h3>
            <p>Generate a PineScript v5 <code>strategy()</code> from your graph and paste it straight into the TradingView editor to chart and forward-test.</p>
          </div>
          <div className="landing-feature-card">
            <h3>Python backtrader</h3>
            <p>Export a standalone backtrader script — data feed, broker, commission, and slippage included — to run and extend locally in Python.</p>
          </div>
          <div className="landing-feature-card">
            <h3>Honest, portable output</h3>
            <p>Every export flags anything that can't translate to a platform, so you always know exactly what you're getting.</p>
          </div>
        </div>
      </section>

      {/* API */}
      <section className="landing-features">
        <div className="landing-section-header">
          <div className="landing-hero-badge" style={{ marginBottom: 16 }}>FOR DEVELOPERS &amp; QUANTS</div>
          <h2>Build on our <span className="teal">API</span></h2>
          <p>
            Need programmatic access? We build custom APIs on the same engine — run backtests,
            pull full trade and performance data, fetch market data (including NSE), and export
            strategies. Usage-based pricing, no upfront cost.
          </p>
        </div>
        <div className="landing-features-grid">
          <div className="landing-feature-card">
            <h3>Built to your spec</h3>
            <p>Tell us the operations and markets you need and we build the exact endpoints for your workflow.</p>
          </div>
          <div className="landing-feature-card">
            <h3>Usage-based pricing</h3>
            <p>No subscription to start — 100 free backtests a month, then pay only for what you run.</p>
          </div>
          <div className="landing-feature-card">
            <h3>Research or automation</h3>
            <p>Backtests, trade logs, equity curves, and market data as clean JSON — for local research or live automation.</p>
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: 28 }}>
          <button className="landing-btn-secondary" onClick={() => navigate('/api-access')}>
            Explore the API →
          </button>
        </div>
      </section>

      {/* CTA */}
      <section className="landing-cta-section">
        <div className="landing-cta-glow" />
        <h2>Ready to backtest <span className="teal">smarter</span>?</h2>
        <p>Free forever for basic use. No credit card required.</p>
        <button className="landing-btn-primary landing-btn-lg" onClick={() => navigate('/auth')}>
          Create Free Account
        </button>
      </section>
    </div>
  );
}
